import type { TFunction } from "i18next";

/**
 * Cloudinary delivery transform.
 *
 * Images are uploaded to Cloudinary as originals — a stock photo is routinely
 * 8192×5464 and 7MB. Served raw, the browser has to decode that into a bitmap
 * (8192 × 5464 × 4B ≈ 179MB) on the MAIN THREAD the moment it scrolls into
 * view, which showed up as 300–500ms scroll stalls in exactly the sections
 * that carry photos. The home page shipped 60MB of images across 48 untransformed
 * Cloudinary URLs.
 *
 * `f_auto` picks avif/webp per browser, `q_auto` picks the quality, `w_` caps the
 * pixel width, `c_limit` never upscales a smaller original. Applied on delivery,
 * so the stored original is untouched and an editor's re-upload still works.
 *
 * Non-Cloudinary URLs (unsplash, framer) pass through unchanged — they carry
 * their own sizing params already.
 */
export const cdnImage = (url: string, width = 1200): string => {
  if (!url.includes("res.cloudinary.com") || !url.includes("/image/upload/")) return url;
  // Already transformed (an editor may have pasted a transformed URL) — leave it.
  if (/\/image\/upload\/[^/]*(?:f_|q_|w_|c_)/.test(url)) return url;
  return url
    .replace("/image/upload/", `/image/upload/f_auto,q_auto,c_limit,w_${width}/`)
    // Drop the `/v<digits>/` segment. A versioned Cloudinary URL is served
    // `cache-control: immutable, max-age=30d`, so replacing the asset in the
    // media library never reaches anyone who already loaded it — the browser
    // won't even revalidate. Versionless serves the current asset and stays
    // revalidatable, which is what we want for content someone can swap.
    .replace(/\/v\d+\//, "/");
};

/**
 * URL of a CMS-managed block image, or the bundled in-repo fallback asset.
 *
 * Editors replace block images in the CMS admin (Pages → image field → Replace),
 * which stores the URL on the image slug (e.g. `home.hero.image` — the `.alt`
 * sibling holds the alt text). `fetch-cms-content.mjs` bakes it into the i18n
 * overlay as a flat key (keySeparator is off). Until an editor uploads one the key
 * is absent and `t()` returns the empty default, so we fall back to the bundled
 * asset and a page is never imageless — the same subtractive contract as sections.
 *
 * `width` caps the delivered pixel width; pass the widest the slot is ever shown
 * at (retina included). Defaults to a full-bleed-safe 1200.
 */
export const cmsImage = (t: TFunction, key: string, fallback: string, width = 1200): string =>
  cdnImage((t(key, { defaultValue: "" }) as string) || fallback, width);
